/**
 * Insight packs — the API client for one game's packs and its facts.
 *
 * A pack is SQL + one HTML page (app/insight_packs/contract.py). It is
 * listed on a game with its eligibility, built on the first open and served
 * from the stored page after (app/routes/insight_packs_api.py).
 */
import { API_BASE, api } from '../lib/api'
import { pathSeg } from '../lib/urls'
import { withViewAs } from '../lib/viewAs'

/** The build row (insight_runs), as the page reads it. */
export type RunRow = {
  id: number
  game_code: string
  pack_id: string
  pack_version: string
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  computed_at: string | null
  started_at: string | null
  finished_at: string | null
  error: string | null
  has_artifact: boolean
  published_at: string | null
  /** null when unpublished; false when the club reads an earlier build
   *  than the newest (an admin rebuilt without publishing). */
  published_page_current: boolean | null
  payload_count: number | null
}

export type PackMissing = { kind: string; name: string; detail: string }

export type PackEntry = {
  name: string
  version: string
  title: string
  description: string
  requires: { tables?: string[] }
  data: string[]
  eligibility: { met: boolean; missing: PackMissing[] }
  last_run: RunRow | null
  /** Whether opening serves the stored page (true) or builds it first. */
  current: boolean
}

export type GamePacks = {
  game_code: string
  served: boolean
  /** Where the game lives — the links out of the page (Match
   *  Administration takes id + club, not the code). */
  game_id?: number | null
  organization_id?: number | null
  /** Set when the game's frame rate cannot be read: no page can be judged
   *  current (every `current` is false) and no build can run. */
  clock_error?: string | null
  pages: PackEntry[]
}

export function listGamePacks(gameCode: string): Promise<GamePacks> {
  return api<GamePacks>(`/api/v1/games/${pathSeg(gameCode)}/packs`)
}

/** The address the viewer frames. Relative so the proxy carries it; the
 *  build id defeats a cache entry a previous release left behind. */
/** The pack's uploaded zip, for any Priori staff to download and iterate
 *  on. A plain navigation (the JWT cookie authenticates it); gated at
 *  platform.read on the server. */
export function packBundleUrl(name: string, version: string): string {
  return `${API_BASE}/api/v1/insights/packs/${pathSeg(name)}/${pathSeg(version)}/bundle`
}

export function packViewUrl(gameCode: string, name: string, version: string): string {
  // framed or opened by the browser itself: this tab's view-as rides the URL
  return withViewAs(`/api/v1/games/${pathSeg(gameCode)}/packs/${pathSeg(name)}`
    + `/${pathSeg(version)}/view?v=${__BUILD_ID__}`)
}

export interface RequeueResult {
  mode: 'all' | 'failed'
  packs: number
  queued: number
  skipped: number
  /** Packs a drainer already owns — left alone, not re-dispatched. */
  already_in_flight: number
  dispatched: number
  failures: { pack: string; detail: string }[]
  runner: string
}

/** Requeue this game's pack builds — rebuilt whatever is stored (`all`) or
 *  only the ones not already succeeded and current (`failed`); `packs`
 *  narrows either to those versions (every registered version when
 *  omitted). Builds run on the insight_runs queue; watch them on Manage
 *  Insights → Builds. Admin. */
export function rebuildGameReports(
  gameCode: string, mode: 'all' | 'failed',
  packs?: { name: string; version: string }[],
): Promise<RequeueResult> {
  return api<RequeueResult>(
    `/api/v1/games/${pathSeg(gameCode)}/reports/rebuild?mode=${mode}`,
    { method: 'POST', ...(packs ? { body: JSON.stringify({ packs }) } : {}) },
  )
}

/** Build the page again now, whatever is stored. Platform-admin. */
export function rebuildPack(
  gameCode: string, name: string, version: string, reason: string,
): Promise<RunRow> {
  return api<RunRow>(
    `/api/v1/games/${pathSeg(gameCode)}/packs/${pathSeg(name)}`
      + `/${pathSeg(version)}/rebuild?reason=${encodeURIComponent(reason)}`,
    { method: 'POST' },
  )
}

export interface BuildAllResult {
  served_games: number
  queued: number
  already_in_flight: number
  reaped: number
  runner: string
  execution: string
  note?: string
}

/** Queue a build of this pack on EVERY served game; one drainer builds
 *  them serially. Platform-admin. */
export function buildPackEverywhere(
  name: string, version: string, force = false,
): Promise<BuildAllResult> {
  return api<BuildAllResult>(
    `/api/v1/insights/packs/${pathSeg(name)}/${pathSeg(version)}/build-all`,
    { method: 'POST', body: JSON.stringify({ force }) },
  )
}

/** Publish a built page to the club's reports, or retire it. */
export function setPackPublished(
  gameCode: string, name: string, version: string, published: boolean, reason: string,
): Promise<RunRow> {
  const verb = published ? 'publish' : 'unpublish'
  return api<RunRow>(
    `/api/v1/games/${pathSeg(gameCode)}/packs/${pathSeg(name)}/${pathSeg(version)}/${verb}`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  )
}

/** Rebuild this pack's page now and point the club at the new build:
 *  the rebuild runs first, then publication moves to the fresh page —
 *  a failed rebuild changes nothing the club sees. Platform-admin. */
export function republishPack(
  gameCode: string, name: string, version: string, reason: string,
): Promise<RunRow> {
  return api<RunRow>(
    `/api/v1/games/${pathSeg(gameCode)}/packs/${pathSeg(name)}/${pathSeg(version)}/republish`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  )
}

// ── game facts ──────────────────────────────────────────────────────────────

export type GameFactsPayload = {
  game_code: string
  facts: {
    pitch: { length_cm: number | null; width_cm: number | null
             source: string | null; assumed?: boolean }
    identity: { home_team_name: string | null; away_team_name: string | null
                tracking_home_index: number | null; source: string | null }
    timing: { fps: number | null; half2_frame: number | null
              source: string | null }
    direction: { home_attack_dir_h1: string | null; source: string | null }
  }
  /** What the AWAY@HOME game code implies. A convention, not a fact. */
  suggested: { home_team_name: string | null; away_team_name: string | null }
  editable: string[]
}

export const getGameFacts = (gameCode: string) =>
  api<GameFactsPayload>(`/api/v1/games/${pathSeg(gameCode)}/facts`)

export const updateGameFacts = (
  gameCode: string, body: Record<string, unknown>,
) => api<GameFactsPayload>(`/api/v1/games/${pathSeg(gameCode)}/facts`, {
  method: 'PATCH', body: JSON.stringify(body),
})
